import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Package, User, Calendar, Check, X, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

const GestionEmprunts = ({ outils, onRefresh }) => {
  const [emprunts, setEmprunts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEmprunt, setShowAddEmprunt] = useState(false);
  const [selectedOutil, setSelectedOutil] = useState('');
  const [emprunteurNom, setEmprunteurNom] = useState('');
  const [dateRetourPrevue, setDateRetourPrevue] = useState('');

  useEffect(() => {
    loadEmprunts();
  }, []);

  const loadEmprunts = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('mat_emprunts')
        .select(`
          *,
          outil:mat_outils(*)
        `)
        .eq('statut', 'en_cours')
        .order('date_emprunt', { ascending: false });

      if (error) throw error;

      setEmprunts(data || []);
    } catch (error) {
      console.error('Erreur chargement emprunts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmprunt = async (e) => {
    e.preventDefault();

    if (!selectedOutil || !emprunteurNom || !dateRetourPrevue) {
      alert('❌ Veuillez remplir tous les champs');
      return;
    }

    try {
      const { error } = await supabase.rpc('mat_emprunter_outil', {
        p_outil_id: selectedOutil,
        p_emprunteur_nom: emprunteurNom,
        p_date_retour_prevue: dateRetourPrevue
      });

      if (error) throw error;

      alert('✅ Outil emprunté avec succès !');
      setShowAddEmprunt(false);
      setSelectedOutil('');
      setEmprunteurNom('');
      setDateRetourPrevue('');
      
      loadEmprunts();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Erreur emprunt:', error);
      alert('❌ Erreur: ' + error.message);
    }
  };

  const handleRetour = async (empruntId) => {
    try {
      const { error } = await supabase.rpc('mat_retourner_outil', {
        p_emprunt_id: empruntId
      });

      if (error) throw error;

      alert('✅ Outil retourné avec succès !');
      loadEmprunts();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Erreur retour:', error);
      alert('❌ Erreur: ' + error.message);
    }
  };

  const outilsDisponibles = outils.filter(o => o.disponible);
  const aujourd_hui = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Emprunts</h2>
          <p className="text-gray-400 mt-1">
            {emprunts.length} emprunt(s) en cours
          </p>
        </div>
        
        <button
          onClick={() => setShowAddEmprunt(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Nouvel emprunt
        </button>
      </div>

      {/* Formulaire nouvel emprunt */}
      {showAddEmprunt && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800 rounded-lg p-6 border border-slate-700"
        >
          <h3 className="text-xl font-semibold text-white mb-4">Nouvel emprunt</h3>
          
          <form onSubmit={handleEmprunt} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Outil à emprunter *
              </label>
              <select
                value={selectedOutil}
                onChange={(e) => setSelectedOutil(e.target.value)}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                required
              >
                <option value="">-- Sélectionner un outil --</option>
                {outilsDisponibles.map((outil) => (
                  <option key={outil.id} value={outil.id}>
                    {outil.nom} - {outil.reference} ({outil.emplacement_nom})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nom de l'emprunteur *
              </label>
              <input
                type="text"
                value={emprunteurNom}
                onChange={(e) => setEmprunteurNom(e.target.value)}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                placeholder="Nom complet"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Date de retour prévue *
              </label>
              <input
                type="date"
                value={dateRetourPrevue}
                onChange={(e) => setDateRetourPrevue(e.target.value)}
                min={aujourd_hui}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                required
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Enregistrer l'emprunt
              </button>
              <button
                type="button"
                onClick={() => setShowAddEmprunt(false)}
                className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Liste des emprunts */}
      <div className="space-y-4">
        {emprunts.length === 0 ? (
          <div className="text-center py-12 bg-slate-800 rounded-lg">
            <Package className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="text-gray-400">Aucun emprunt en cours</p>
          </div>
        ) : (
          emprunts.map((emprunt) => {
            const dateRetour = new Date(emprunt.date_retour_prevue);
            const maintenant = new Date();
            const enRetard = dateRetour < maintenant;
            const joursRestants = Math.ceil((dateRetour - maintenant) / (1000 * 60 * 60 * 24));

            return (
              <motion.div
                key={emprunt.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`bg-slate-800 rounded-lg p-6 border-l-4 ${
                  enRetard ? 'border-red-500' : 'border-blue-500'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Package className="w-6 h-6 text-blue-400" />
                      <h3 className="text-xl font-semibold text-white">
                        {emprunt.outil?.nom || 'Outil inconnu'}
                      </h3>
                      {enRetard && (
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm font-medium">
                          <AlertTriangle className="w-4 h-4 inline mr-1" />
                          En retard
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400 flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Emprunteur
                        </p>
                        <p className="text-white font-medium">{emprunt.emprunteur_nom}</p>
                      </div>

                      <div>
                        <p className="text-gray-400 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Date d'emprunt
                        </p>
                        <p className="text-white font-medium">
                          {new Date(emprunt.date_emprunt).toLocaleDateString('fr-FR')}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-400 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Retour prévu
                        </p>
                        <p className={`font-medium ${enRetard ? 'text-red-400' : 'text-white'}`}>
                          {dateRetour.toLocaleDateString('fr-FR')}
                          {!enRetard && joursRestants > 0 && (
                            <span className="text-gray-400 ml-2">
                              ({joursRestants} jour{joursRestants > 1 ? 's' : ''})
                            </span>
                          )}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-400">Référence</p>
                        <p className="text-white font-medium">
                          {emprunt.outil?.reference || '-'}
                        </p>
                      </div>
                    </div>

                    {emprunt.notes && (
                      <div className="mt-4 p-3 bg-slate-700/50 rounded">
                        <p className="text-sm text-gray-400">Notes:</p>
                        <p className="text-white">{emprunt.notes}</p>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleRetour(emprunt.id)}
                    className="ml-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Marquer comme retourné
                  </button>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default GestionEmprunts;